// components/ui/Select.jsx
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Check, Search, X } from "lucide-react";
import { cn } from "../../utils/cn";
import Button from "./Button";
import Input from "./Input";

const MENU_MAX_HEIGHT = 240;

const Select = React.forwardRef(({
    className,
    options = [],
    value,
    placeholder = "Select an option",
    multiple = false,
    disabled = false,
    required = false,
    label,
    description,
    error,
    searchable = false,
    clearable = false,
    loading = false,
    id,
    name,
    onChange,
    onOpenChange,
    ...props
}, ref) => {

    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [menuStyle, setMenuStyle] = useState(null);
    const containerRef = useRef(null);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);

    const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;

    // Filter options for search
    const filteredOptions = searchable && searchTerm
        ? options?.filter(option =>
            option?.label?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
            option?.value?.toString()?.toLowerCase()?.includes(searchTerm.toLowerCase())
        )
        : options;

    // Display selected label(s)
    const getSelectedDisplay = () => {
        if (!value) return placeholder;

        if (multiple) {
            if (!value?.length) return placeholder;
            if (value.length === 1) return value[0]?.label;
            return `${value.length} items selected`;
        }

        return value?.label || placeholder;
    };

    const handleToggle = () => {
        if (disabled) return;

        const newIsOpen = !isOpen;
        setIsOpen(newIsOpen);
        onOpenChange?.(newIsOpen);

        if (searchable) {
            setSearchTerm("");
        }
    };

    const updateMenuPosition = () => {
        const trigger = triggerRef.current;
        if (!trigger) return;

        const rect = trigger.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const estimatedHeight = Math.min(
            MENU_MAX_HEIGHT + (searchable ? 56 : 0),
            Math.max(spaceBelow, spaceAbove) - 8,
        );
        const openUpward = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;

        setMenuStyle({
            position: "fixed",
            left: rect.left,
            width: rect.width,
            zIndex: 10000,
            maxHeight: estimatedHeight,
            ...(openUpward
                ? { bottom: window.innerHeight - rect.top + 4 }
                : { top: rect.bottom + 4 }),
        });
    };

    useLayoutEffect(() => {
        if (!isOpen) {
            setMenuStyle(null);
            return undefined;
        }

        updateMenuPosition();

        const handleReposition = () => updateMenuPosition();
        window.addEventListener("resize", handleReposition);
        window.addEventListener("scroll", handleReposition, true);

        return () => {
            window.removeEventListener("resize", handleReposition);
            window.removeEventListener("scroll", handleReposition, true);
        };
    }, [isOpen, searchable, filteredOptions?.length, searchTerm]);

    useEffect(() => {
        if (!isOpen) return undefined;

        const handlePointerDown = (event) => {
            const target = event.target;
            const inTrigger = containerRef.current?.contains(target);
            const inMenu = menuRef.current?.contains(target);
            if (!inTrigger && !inMenu) {
                setIsOpen(false);
                setSearchTerm("");
                onOpenChange?.(false);
            }
        };

        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("touchstart", handlePointerDown);

        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("touchstart", handlePointerDown);
        };
    }, [isOpen, onOpenChange]);

    const handleOptionSelect = (option) => {
        if (multiple) {
            const current = value || [];
            const exists = current?.find(v => v?.value === option?.value);

            const updated = exists
                ? current.filter(v => v?.value !== option?.value)
                : [...current, option];

            onChange?.(updated);
        } else {
            onChange?.(option);
            setIsOpen(false);
            setSearchTerm("");
            onOpenChange?.(false);
        }
    };

    const handleClear = (e) => {
        e.stopPropagation();
        setSearchTerm("");
        onChange?.(multiple ? [] : null);
    };

    const isSelected = (optionValue) => {
        if (multiple) {
            return value?.some(v => v?.value === optionValue) || false;
        }
        return value?.value === optionValue;
    };

    const hasValue = multiple
        ? value?.length > 0
        : value !== undefined && value !== null;

    const setTriggerRef = (node) => {
        triggerRef.current = node;
        if (typeof ref === "function") {
            ref(node);
        } else if (ref) {
            ref.current = node;
        }
    };

    const dropdownMenu = isOpen && menuStyle ? createPortal(
        <div
            ref={menuRef}
            style={menuStyle}
            className="overflow-hidden rounded-md border border-border bg-background text-foreground shadow-md"
        >
            {searchable && (
                <div className="border-b border-border p-2">
                    <div className="relative">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search options..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-8 pr-8"
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                onClick={() => setSearchTerm("")}
                                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground transition-colors hover:text-foreground"
                                aria-label="Clear search"
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>
                        )}
                    </div>
                </div>
            )}

            <div className="max-h-60 overflow-auto py-1">
                {filteredOptions?.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-muted-foreground">
                        {searchTerm
                            ? "No options found"
                            : "No options available"}
                    </div>
                ) : (
                    filteredOptions?.map(option => (
                        <div
                            key={option?.value}
                            className={cn(
                                "flex cursor-pointer items-center rounded-sm px-3 py-2 text-sm transition-colors hover:bg-muted",
                                isSelected(option?.value) &&
                                "bg-[#16325a] text-white hover:bg-[#16325a]",
                                option?.disabled &&
                                "pointer-events-none opacity-50"
                            )}
                            onClick={() =>
                                !option?.disabled &&
                                handleOptionSelect(option)
                            }
                        >
                            <span className="flex-1">
                                {option?.label}
                            </span>

                            {isSelected(option?.value) && (
                                <Check className="h-4 w-4" />
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>,
        document.body,
    ) : null;

    return (
        <div ref={containerRef} className={cn("relative", className)}>
            {label && (
                <label
                    htmlFor={selectId}
                    className={cn(
                        "text-sm font-medium mb-2 block",
                        error ? "text-destructive" : "text-foreground"
                    )}
                >
                    {label}
                    {required && <span className="text-destructive ml-1">*</span>}
                </label>
            )}

            <div className="relative">
                <button
                    ref={setTriggerRef}
                    id={selectId}
                    type="button"
                    className={cn(
                        "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground transition-[border-color,box-shadow] focus-visible:outline-none focus-visible:border-[#16325a] focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:shadow-[0_0_0_3px_rgba(22,50,90,0.12)]",
                        error && "border-destructive focus-visible:border-destructive focus-visible:shadow-[0_0_0_3px_rgba(220,38,38,0.12)]",
                        !hasValue && "text-muted-foreground",
                        disabled && "opacity-50 cursor-not-allowed"
                    )}
                    onClick={handleToggle}
                    disabled={disabled}
                    aria-expanded={isOpen}
                    aria-haspopup="listbox"
                    {...props}
                >
                    <span className="truncate">{getSelectedDisplay()}</span>

                    <div className="flex items-center gap-1">
                        {loading && (
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            </svg>
                        )}

                        {clearable && hasValue && !loading && (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-4 w-4"
                                onClick={handleClear}
                            >
                                <X className="h-3 w-3" />
                            </Button>
                        )}

                        <ChevronDown
                            className={cn(
                                "h-4 w-4 transition-transform",
                                isOpen && "rotate-180"
                            )}
                        />
                    </div>
                </button>

                {/* Hidden select for forms */}
                <select
                    name={name}
                    value={
                        multiple
                            ? value?.map(v => v?.value)
                            : value?.value || ''
                    }
                    readOnly
                    className="sr-only"
                    multiple={multiple}
                    required={required}
                >
                    <option value="">Select...</option>
                    {options?.map(option => (
                        <option key={option?.value} value={option?.value}>
                            {option?.label}
                        </option>
                    ))}
                </select>

                {dropdownMenu}
            </div>

            {description && !error && (
                <p className="text-sm text-muted-foreground mt-1">
                    {description}
                </p>
            )}

            {error && (
                <p className="text-sm text-destructive mt-1">
                    {error}
                </p>
            )}
        </div>
    );
});

Select.displayName = "Select";

export default Select;
